/*
 * Copyright (c) 2020 NOOGA DEVELOPMENT, All rights reserved.
 * Author Raman Kremko <raman.kremko@noogadev.com>
 */

import {
    InspectionResult,
    InspectionStatus,
    ServiceOperatorDto,
    STORE_STATUSES,
    StoreCashbox,
    StoreCoordinatesHistoryItemDto,
    StoreDetailsDto,
    StoreDetailsForMap,
    StoreInspectionDto,
    StorePhotoInfoDto,
    StorePoint,
    StoresListItemDto,
    StoreType,
} from './storesClientTypings';
import { factoryT, factoryTBuilder, fields } from 'factory-t';
import { legalAddressFactory } from '../operators/operatorsFactories';
import { CASH_BOXES_STATUSES, CashBoxesStats } from '../cashBoxes/cashBoxesClient.typings';
import { Currency, RegistryTurnoversByCashBox } from '../apiCommon.typings';
import { turnoversPartialDtoFactory } from '../reports/reportsFactories';
import storePreview from '../../../assets/images/main-page-background.png';
import { StoreLowerActivity, StoreMainActivity, StoreMiddleActivity } from '../shortLists/shortListsClient.typings';

export const storeListItemDtoFactory = factoryTBuilder<StoresListItemDto>({
    taxNumber: (ctx) => `20049471611100${ctx.index}`,
    name: fields.string(),
    storeType: fields.sequence(Object.values(StoreType)),
    fullAddress: 'Minsk, Mstislavtca, 11',
    status: fields.sequence(Object.values(STORE_STATUSES)),
    taxAuthority: 'Tax authority-1',
    taxationTypes: ['VAT', 'INCOME', 'PROFIT', 'SIMPLIFIED'],
    cashBoxes: {
        online: 12,
        offline: 10,
    },
    company: {
        name: 'Company #1',
        taxNumber: '43234323432',
    },
    mainActivity: fields.sequence(Object.values(StoreMainActivity)),
    middleActivity: fields.sequence(Object.values(StoreMiddleActivity)),
    lowerActivity: fields.sequence(Object.values(StoreLowerActivity)),
})
    .setFieldFactory('name', (ctx) => `Store ${ctx.inject('taxNumber')}`)
    .factory();

const serviceOperatorsFactory = factoryT<ServiceOperatorDto>({
    name: (ctx) => `Random${ctx.index}Corparated`,
    taxNumber: (ctx) => `1945872000${ctx.index}`,
});

export const storeDetailsFactory = factoryT<StoreDetailsDto>({
    taxNumber: (ctx) => `20049471611100${ctx.index}`,
    name: 'Random13Corparated',
    address: legalAddressFactory.item(),
    storeType: StoreType.CULTURAL,
    fullAddress: 'Region11 Locale1 City5region11 Street1city84 building 1',
    serviceOperators: serviceOperatorsFactory.list({ count: 2 }),
    companyTaxNumber: (ctx) => `89567375482039487${ctx.index}`,
    taxationTypes: ['VAT', 'INCOME', 'PROFIT', 'SIMPLIFIED'],
    status: fields.sequence(Object.values(STORE_STATUSES)),
    taxAuthority: (ctx) => `${ctx.index} saylı Ərazi Vergilər İdarəsi`,
    companyName: 'companyName1',
    lng: 40.7143528,
    lat: 30.0059731,
    mainActivity: fields.sequence(Object.values(StoreMainActivity)),
    middleActivity: fields.sequence(Object.values(StoreMiddleActivity)),
    lowerActivity: fields.sequence(Object.values(StoreLowerActivity)),
});

export const storeCashBoxesFactory = factoryT<StoreCashbox>({
    taxNumber: (ctx) => `20049471611100${ctx.index}`,
    lastDocumentDateAtUtc: '2020-02-02T11:27:11.996063',
    operatorTaxNumber: (ctx) => `${35644856946294 + ctx.index}`,
    operatorName: (ctx) => `Operator #${ctx.index}`,
    online: fields.sequence([false, false, true, true, true]),
    validTime: fields.sequence([false, true, false, true, true]),
    status: fields.sequence([...CASH_BOXES_STATUSES]),
    cashRegisterModelName: 'Model-2106-110',
    storeType: fields.sequence([StoreType.CELEBRATIONS, StoreType.COMPUTERS]),
});

export const coordinatesHistoryItemFactory = factoryT<StoreCoordinatesHistoryItemDto>({
    lng: (ctx) => 40.7143528 + ctx.index,
    lat: (ctx) => -74.0059731 + ctx.index,
    insertedAtUtc: '2023-02-02T11:27:11.996063Z',
    userName: fields.sequence(['Petrov', 'Ivanov']),
});

export const inspectionsFactory = factoryT<StoreInspectionDto>({
    inspectionId: (ctx) => ctx.index.toString(),
    status: fields.sequence([
        InspectionStatus.OPEN,
        InspectionStatus.CLOSED,
        InspectionStatus.CLOSED,
        InspectionStatus.CLOSED,
        InspectionStatus.CLOSED,
    ]),
    initiatorUserName: fields.sequence(['Petrov', 'Ivanov']),
    insertedAtUtc: '2023-02-02T11:27:11.996063Z',
    initiatorComment: (ctx) => `Control purchase_${ctx.index} `,
    auditorName: fields.sequence(['Sidorov', 'Kuznetsov']),
    assignedAtUtc: '2023-02-02T11:27:11.996063Z',
    closedAtUtc: '2023-02-02T11:27:11.996063Z',
    productName: 'Bread',
    inspectedAtUtc: '2023-02-02T11:27:11.996063Z',
    result: fields.sequence([
        InspectionResult.RECEIPT_OK,
        InspectionResult.RECEIPT_OK,
        InspectionResult.NO_RECEIPT,
        InspectionResult.STORE_CLOSED,
    ]),
    resultComment:
        'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
    photos: [],
});

export const storeCashBoxesStatsFactory = factoryT<CashBoxesStats>({
    states: {
        active: 9,
        blocked: 9,
        inactive: 13,
        notBlocked: 10,
        offline: 19,
        online: 22,
    },
    statuses: {
        registration: 5,
        active: 6,
        blocked: 4,
        reset: 2,
        broken: 0,
        service: 0,
        eliminated: 1,
    },
    types: {
        temporary: 2,
        primary: 18,
    },
    timers: {
        correct: 38,
        incorrect: 3,
    },
});

export const turnoversByCashBoxFactory = factoryT<RegistryTurnoversByCashBox>({
    taxNumber: (ctx) => `${2035620000 + ctx.index}`,
    ...turnoversPartialDtoFactory.item(),
    currency: Currency.AZN,
    vatSum: 20390,
    turnover: 4000000,
});

export const storeDetailsForMapFactory = factoryT<StoreDetailsForMap>({
    cashBoxesOnline: 10,
    cashBoxesTotal: 15,
    name: (ctx) => `Random${ctx.index}Corparated`,
    taxNumber: (ctx) => `20049471611100${ctx.index}`,
    type: fields.sequence(Object.values(StoreType)),
    url: storePreview,
});

export const storeOnMapFactory = factoryT<StorePoint>({
    taxNumber: (ctx) => `200494716311${ctx.index}`.substring(0, 15),
    lat: 0,
    lon: 0,
});

export const storePhotoInfoFactory = factoryT<StorePhotoInfoDto>({
    name: (ctx) => `photo-${ctx.index}.png`,
    userName: fields.sequence(['Petrov', 'Ivanov']),
    insertedAtUtc: '2023-02-02T11:27:11.996063Z',
    preview: fields.sequence([true, false, false]),
    userId: (ctx) => ctx.index + 1,
    url: (ctx) => `https://example.com/photo-${ctx.index}.png`,
    presignedPutObjectUrl: (ctx) => `https://example.com/upload/photo-${ctx.index}`,
});
